import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { ActivatedRoute } from '@angular/router';
import { DateTime } from 'luxon';
import moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap, takeUntil } from 'rxjs/operators';

// Interfaces
import { LocationWarehouseApiService } from '../../../shared/services/api/location/location-warehouse-api.service';
import { UserApiService } from '../../../shared/services/api/user-api.service';
import { trackByFilterId, trackByValue } from '../../../shared/util';
import { IFilterConfig } from '../../interfaces/filter.interface';

// Config

// Services

@Component({
  selector: 'lum-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
})
export class FilterComponent implements OnInit {
  @Input()
  filterId :string = '';

  @Input()
  filters :IFilterConfig[] = [];

  @Input()
  buttonClass :string = '';

  @Input()
  buttonIconColor :string = '';

  @Output()
  queryBuildEvent :EventEmitter<any> = new EventEmitter<any>();

  values :Record<string, any> = {};

  private onDestroy$ = new Subject();
  private keywordUpdated$ = new Subject<{ id :string; keyword :string }>();

  trackByValue = trackByValue;
  trackByFilterId = trackByFilterId;

  constructor(
    private route :ActivatedRoute,
    private cd :ChangeDetectorRef,
    private locationWarehouseApiService :LocationWarehouseApiService,
    private userApiService :UserApiService,
  ) {}

  ngOnInit() :void {
    this.initActions();
    this.getQueryParamsFromUrl();
    this.onKeywordUpdate().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onEmitChange(id :string, event :any) :void {
    const values = Object.assign({}, this.values);
    values[id] = event?.target?.value || event;
    this.values = values;
    this.onUpdateFilters();
  }

  onSelectMenu(event :MatCheckboxChange, filter :IFilterConfig) :void {
    const index = this.displayFilters.findIndex((row) => row.id === filter.id);
    const selectedFilter = this.displayFilters[index];
    selectedFilter.show = event.checked;
    if (
      selectedFilter.hasOwnProperty('config') &&
      selectedFilter.config.hasOwnProperty('default')
    ) {
      this.values[selectedFilter.id] = selectedFilter.config.default;
    }
    const queryParams = this.buildQueryParams();
    this.setFilterStorage(this.filteredFiltersAsStringArray, queryParams);
  }

  onInputChange(id :string, event :any) :void {
    this.keywordUpdated$.next({
      id: id,
      keyword: event.target.value,
    });
  }

  onDateChange(id :string, event :MatDatepickerInputEvent<Date>) :void {
    const date = moment.isMoment(event.value)
      ? DateTime.fromJSDate(event.value.toDate()).toISODate()
      : moment(event.value, 'YYYY-MM-DD').toISOString();
    this.onEmitChange(id, date);
  }

  onClearDate(filter :IFilterConfig) :void {
    this.values[filter.id] = '';
    this.onEmitChange(filter.id, '');
  }

  onCheckboxChange(id :string, event :MatCheckboxChange) :void {
    this.onEmitChange(id, event.checked ? 1 : 0);
  }

  onTagFilterSelect(id :string, event :any) :void {
    this.onEmitChange(id, event?.tag || []);
  }

  onLocationFilterSelect(id :string, event :any) :void {
    this.onEmitChange(id, event?.location || []);
  }

  onRemoveFilter(index :number) :void {
    const filter = this.filteredFilters[index];

    filter.show = false;
    if (this.values.hasOwnProperty(filter.query)) {
      delete this.values[filter.query];
    }

    this.onUpdateFilters();
  }

  private initActions() :void {
    if (this.hasFilter('users')) {
      this.userApiService.get({});
    }
    if (this.hasFilter('locations')) {
      this.locationWarehouseApiService.fetchWarehouses({
        all: '1',
        parent_id: '0',
      });
    }
  }

  private buildQueryParams() :any {
    return this.parseObjectToQuery(this.values);
  }

  private parseObjectToQuery(data :any) :any {
    // TODO: set interface
    return Object.keys(data).reduce((obj, next) => {
      const filter = this.mappedFilters[next];
      const value = this.values[next];

      if (!filter) {
        return { ...obj };
      }

      if (filter?.type === 'category' && filter?.config?.isSubcategory) {
        if (this.values[filter?.config?.syncedWith] === '') {
          return obj;
        }
      }

      if (Array.isArray(value)) {
        return {
          ...obj,
          [filter.query as string]: value?.join(','),
        };
      }

      return {
        ...obj,
        [filter.query as string]: value?.toString(),
      };
    }, {});
  }

  private getCurrentQueryParams() :void {
    const { params, filters } = this.filterStorageParams;

    if (this.filterId && params && Object.keys(params).length > 0) {
      const keys = Object.keys(params || {});
      this.filters = this.filters.map((filter :IFilterConfig) => {
        return {
          ...filter,
          show: keys.includes(filter.id) || filters?.includes(filter.id),
        };
      });

      if (Object.keys(params).length > 0) {
        this.values = params;
        this.setUrlQueryParams(params);
      }
    }

    this.cd.detectChanges();
    this.queryBuildEvent.emit({ query: this.buildQueryParams() });
  }

  private onKeywordUpdate() :Observable<{ id :string; keyword :string }> {
    return this.keywordUpdated$.asObservable().pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap((params :{ id :string; keyword :string }) => {
        this.onEmitChange(params.id, params.keyword);
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private onUpdateFilters() :void {
    const queryParams = this.buildQueryParams();
    this.setUrlQueryParams(queryParams);
    this.setFilterStorage(this.filteredFiltersAsStringArray, queryParams);
    this.queryBuildEvent.emit({ query: queryParams });
  }

  private setFilterStorage(filters :string[], params :any) :void {
    if (this.filterId) {
      localStorage.setItem(
        this.filterId,
        JSON.stringify({
          params,
          filters,
        }),
      );
    }
  }

  private setUrlQueryParams(params :any) :void {
    let updatedUrl = window.location.pathname;

    if (Object.keys(params).length > 0) {
      updatedUrl =
        window.location.pathname + '?' + new URLSearchParams(params).toString();
    }

    history.pushState(params, '', updatedUrl);
  }

  private getQueryParamsFromUrl() :void {
    this.route.queryParams.subscribe((params) => {
      if (Object.keys(params).length > 0) {
        const keys = Object.keys(params || {});
        this.values = params;
        this.filters = this.filters.map((filter :IFilterConfig) => {
          return {
            ...filter,
            show: keys.includes(filter.id),
          };
        });
        this.setUrlQueryParams(params);
        this.setFilterStorage(this.filteredFiltersAsStringArray, params);
      }
      this.getCurrentQueryParams();
    });
  }

  private hasFilter(filterType :string) :boolean {
    return this.displayFilters.some((filter) => filter.type === filterType);
  }

  get filterStorageParams() :any {
    if (this.filterId) {
      const storedValue = localStorage.getItem(this.filterId);

      return storedValue ? JSON.parse(storedValue) : {};
    }
  }

  get mappedFilters() :any {
    const filters :any = {};
    this.filteredFilters.forEach((filter) => {
      filters[filter.id] = filter;
    });

    return filters;
  }

  get filteredFilters() :IFilterConfig[] {
    return this.displayFilters.filter((filter) => filter.show);
  }

  get filteredFiltersAsStringArray() :string[] {
    return this.filteredFilters.map((filter) => filter.id);
  }

  get displayFilters() :IFilterConfig[] {
    return this.filters as IFilterConfig[];
  }
}
